// An account created for a single survey, deleted as soon as it submits it.
// It sits below every other role because it holds no privileges at all — the
// one survey it was assigned is the entire extent of what it can reach.
export const ONE_TIME_ROLE = 'one_time';

// How many of them may be created in one go. Mirrors MAX_BULK in the API and
// caps the paste box on the create-user modal.
export const MAX_ONE_TIME_ACCOUNTS = 50;

// Role hierarchy (high privilege -> low). Mirrors ROLE_ORDER in the API.
export const ROLE_ORDER = { one_time: -1, inspector: 0, manager: 1, admin: 2, dev: 3 };

export const ROLE_LABELS = {
  one_time: 'One-Time User',
  inspector: 'Inspector',
  manager: 'Manager',
  admin: 'Admin',
  dev: 'Dev',
};

// What an account with no manager reports to. The admins of a company
// supervise as a group rather than individually, so this names a section of
// the Team tab and the default choice in the supervisor picker — there is no
// single account behind it.
export const ADMIN_GROUP_LABEL = 'Admins';

// Only managers can be picked as a supervisor. An admin account is not one on
// its own; leaving the picker blank puts the new account under the admins.
export const supervisorOptions = (users, companyId) =>
  users.filter((u) => u.role === 'manager' && u.company_id === companyId);

export const isOneTime = (role) => role === ONE_TIME_ROLE;

// An unrecognized role ranks below everything, including one-time users, so a
// role this build doesn't know about is never mistaken for one it does.
const roleRank = (role) => ROLE_ORDER[role] ?? Number.NEGATIVE_INFINITY;

// Whether `role` is at least as privileged as `min`.
const isAtLeast = (role, min) => roleRank(role) >= roleRank(min);

// Roles allowed to manage a team / create users (manager, admin, dev).
export const canManageTeam = (role) => isAtLeast(role, 'manager');

// Roles allowed to create/edit/delete forms (manager, admin, dev).
export const canManageForms = (role) => isAtLeast(role, 'manager');

// The Templates tab: whoever builds forms lays out the pages they print to.
// Mirrors the role check on the templates blueprint.
export const canManageTemplates = (role) => isAtLeast(role, 'manager');

// The Billing tab: managers get their plan and their team's usage, admins and
// devs get the money too. Mirrors the role checks in the billing blueprint.
export const canViewBilling = (role) => isAtLeast(role, 'manager');
export const canSeeBillingAmounts = (role) => isAtLeast(role, 'admin');

// The roles an actor is allowed to create (strictly below their own).
// One-time users come last: the list reads as the hierarchy first, with the
// guest pass after it.
export const creatableRoles = (role) =>
  ['inspector', 'manager', 'admin', ONE_TIME_ROLE].filter((r) => roleRank(r) < roleRank(role));

// Whether `actor` may delete `target`. Mirrors delete_user() in the API so a
// Remove button is only offered where the request would actually succeed —
// notably not on a fellow admin, who is a peer rather than a report.
export const canDeleteUser = (actor, target) => {
  if (!actor || !target || actor.id === target.id) return false;
  if (actor.role === 'dev') return true;
  if (actor.role === 'admin') {
    return target.company_id === actor.company_id && roleRank(target.role) < roleRank('admin');
  }
  if (actor.role === 'manager') {
    return (
      (target.role === 'inspector' || target.role === ONE_TIME_ROLE) &&
      target.manager_id === actor.id
    );
  }
  return false;
};

// Whether `actor` may reissue and read `target`'s one-time password. Mirrors
// can_manage_credentials() in the API so the button only appears where the
// request would actually succeed.
export const canManageCredentials = (actor, target) => {
  if (!actor || !target || actor.id === target.id || target.role === 'dev') return false;
  if (actor.role === 'dev') return true;
  if (actor.role === 'admin') {
    return target.company_id === actor.company_id && roleRank(target.role) < roleRank('admin');
  }
  if (actor.role === 'manager') {
    return (
      (target.role === 'inspector' || target.role === ONE_TIME_ROLE) &&
      target.manager_id === actor.id
    );
  }
  return false;
};
